import React from 'react';
import DimensionToLink from 'app/components/links/DimensionToLink';
import NmsInterfaceLink from 'app/components/links/NmsInterfaceLink';
import DeviceLink from 'app/components/links/DeviceLink';
import { formatAsn } from '@kentik/ui-shared/util/asn';
import { getEntityData } from './nmsAlertEntity';

const DIMENSIONS = {
  deviceId: 'device_id',
  ifIndex: 'ifindex',
  deviceName: 'device_name',
  peerAs: 'peer-as'
};

const getMetricDimension = ({ dimension, alertModel, $metrics }) => {
  const targetMeasurements = Object.keys(alertModel.nmsAlarmContext?.alarmTargets || {});
  // It doesn't matter which measurement we choose here, as any dimension listed on an alert must be common to all alert measurements
  const measurement = targetMeasurements[0];
  const measurementModel = $metrics.measurementModel(measurement);
  if (!measurement || !measurementModel) {
    return null;
  }

  const dimensionKey = dimension.replace($metrics.getAppProtocolDimension(measurement), '');

  const mergedMetricsDimensions = measurementModel.mergedMetricsDimensions[dimensionKey];

  if (!mergedMetricsDimensions) {
    return null;
  }

  const { Label, name } = mergedMetricsDimensions;
  return { label: Label || name, name };
};

export const getNmsAlertDimensionDisplay = ({ $alerting, $metrics, $setup, dimension, value, alertModel }) => {
  if (!dimension || !alertModel) {
    return null;
  }

  let displayValue = value;
  let dimensionKey = dimension;
  let dimensionLabel = $alerting.getDimensionLabel(dimension);

  // '<empty>' is display-only; link data keeps an empty value so no link is built for it
  const fallbackValue = '<empty>';
  const hasValue = Boolean(value);
  const dimensionValue = hasValue ? value : fallbackValue;
  let dimensionToValue = dimensionValue;

  displayValue = $alerting.lookupValue(alertModel.get(), dimension, dimensionValue);

  let overrideContent;

  const metricDimension = getMetricDimension({ dimension, alertModel, $metrics });
  const entityData = getEntityData(alertModel);
  const { interface: entityInterface, device: entityDevice } = entityData || {};

  if (metricDimension) {
    const { label, name } = metricDimension;
    dimensionKey = name;
    dimensionLabel = label;
    dimensionToValue = { [name]: hasValue ? displayValue : '' };
  } else if (dimension === DIMENSIONS.deviceId && hasValue) {
    /**
     This is a special case where we are getting this dimension information in a different format than expected by DimensionToLink.
     Here, we receive device ID under the dimension device_id.
     DimensionToLink will try to render a regular (non-NMS) device link for that dimension. It expects a device name in order to render an NMS device link.
     */
    overrideContent = value ? <DeviceLink deviceId={value} name={entityDevice?.name} /> : dimensionValue;
  } else if (dimension === DIMENSIONS.deviceName) {
    /**
     This is a special case where we are getting this dimension information in a different format than expected by DimensionToLink.
     Here, we receive device ID under the dimension device_id.
     DimensionToLink will try to render a regular (non-NMS) device link for that dimension. It expects a device name in order to render an NMS device link.
     */
    overrideContent = <DeviceLink name={value} />;
  } else if (dimension === DIMENSIONS.peerAs && value !== null && value !== undefined && value !== '') {
    // The AS number is stored as asplain; only the rendered form honors the user's notation preference.
    displayValue = formatAsn(value, $setup?.asnNotation);
  } else if (dimension === DIMENSIONS.ifIndex && entityInterface && entityDevice) {
    dimensionLabel = 'Interface';
    overrideContent = (
      <NmsInterfaceLink deviceId={entityDevice.id} snmpId={value}>
        {entityInterface.name || value}
        {entityInterface.description ? `(${entityInterface.description})` : ''}
      </NmsInterfaceLink>
    );
  }

  return {
    label: dimensionLabel,
    value: overrideContent || (
      <DimensionToLink dimension={dimensionKey} dimensionToValue={dimensionToValue}>
        {displayValue}
      </DimensionToLink>
    )
  };
};
